import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";

const errorCopy: Record<string, string> = {
  missing: "Sign-in link was missing or malformed.",
  expired: "That sign-in link has expired. Request a new one below.",
  used: "That sign-in link has already been used.",
  forbidden: "This email is not authorized to access the admin panel.",
};

export default function AdminLogin() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const errKey = params.get("error");

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Magic-link fallback
  const [showLink, setShowLink] = useState(false);
  const [linkEmail, setLinkEmail] = useState("");
  const [linkSent, setLinkSent] = useState(false);
  const [linkBusy, setLinkBusy] = useState(false);

  async function onPasswordSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await api.adminLoginPassword(email.trim(), password);
      navigate("/admin", { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not sign in");
    } finally {
      setSubmitting(false);
    }
  }

  async function onLinkSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLinkBusy(true);
    setError(null);
    try {
      await api.adminLogin(linkEmail.trim());
      setLinkSent(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not send link");
    } finally {
      setLinkBusy(false);
    }
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-stone-100 px-4">
      <div className="card p-8 w-full max-w-md">
        <h1 className="text-xl mb-2 font-serif">Henry Miller Museum · Admin sign-in</h1>
        <p className="text-sm text-museum-muted mb-4">
          Sign in with your email and password.
        </p>

        {errKey && errorCopy[errKey] && (
          <div className="text-sm bg-amber-50 border border-amber-200 text-amber-800 rounded p-2 mb-3">
            {errorCopy[errKey]}
          </div>
        )}

        <form onSubmit={onPasswordSubmit} className="space-y-3">
          <div>
            <label className="label" htmlFor="email">Email</label>
            <input id="email" type="email" required autoComplete="username" className="input"
                   value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>
          <div>
            <label className="label" htmlFor="password">Password</label>
            <input id="password" type="password" required autoComplete="current-password" className="input"
                   value={password} onChange={(e) => setPassword(e.target.value)} />
          </div>
          {error && (
            <div className="text-sm bg-amber-50 border border-amber-200 text-amber-800 rounded p-2">{error}</div>
          )}
          <button type="submit" className="btn-primary w-full" disabled={submitting}>
            {submitting ? "Signing in…" : "Sign in"}
          </button>
        </form>

        <div className="mt-6 pt-4 border-t border-stone-200">
          {!showLink ? (
            <button className="text-sm text-museum-muted underline" onClick={() => setShowLink(true)}>
              Trouble signing in? Email me a one-time link instead
            </button>
          ) : linkSent ? (
            <div className="text-sm bg-green-50 border border-green-200 text-green-800 rounded p-3">
              If <strong>{linkEmail}</strong> is authorized, a sign-in link has been emailed.
              The link is valid for 15 minutes.
            </div>
          ) : (
            <form onSubmit={onLinkSubmit} className="space-y-2">
              <label className="label" htmlFor="linkEmail">Email a sign-in link to</label>
              <input id="linkEmail" type="email" required className="input"
                     value={linkEmail} onChange={(e) => setLinkEmail(e.target.value)} />
              <button type="submit" className="btn-secondary w-full" disabled={linkBusy}>
                {linkBusy ? "Sending…" : "Email me a sign-in link"}
              </button>
            </form>
          )}
        </div>

        <p className="text-xs text-museum-muted mt-6">
          <Link to="/">← Back to public site</Link>
        </p>
      </div>
    </div>
  );
}
